import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { paymentAPI, authAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, Wallet, History, User, LogOut, Menu, X, Home,
  DollarSign, ArrowUpRight, ArrowDownRight, Copy, CheckCircle, Clock, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

function Sidebar({ open, setOpen }) {
  const location = useLocation();
  const { logout } = useAuth();

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { path: '/wallet', label: 'Wallet', icon: <Wallet className="w-5 h-5" /> },
    { path: '/history', label: 'Trade History', icon: <History className="w-5 h-5" /> },
    { path: '/account', label: 'Account', icon: <User className="w-5 h-5" /> },
  ];

  return (
    <>
      {open && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setOpen(false)} 
        />
      )}
      
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 
        bg-[#0f0f11] border-r border-zinc-800 
        transform ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        transition-transform duration-300 flex flex-col
      `}>
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-['Outfit']">GeneiProFx</span>
            </Link>
            <button onClick={() => setOpen(false)} className="lg:hidden text-zinc-400">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setOpen(false)}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <button
            onClick={logout}
            className="nav-item w-full text-red-400 hover:bg-red-500/10"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

export default function WalletPage() {
  const { user, updateUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('deposit');
  const [transactions, setTransactions] = useState([]);
  const [walletAddress, setWalletAddress] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Deposit state
  const [depositAmount, setDepositAmount] = useState('100');
  const [depositTxHash, setDepositTxHash] = useState('');
  const [depositProof, setDepositProof] = useState(null);
  const [depositLoading, setDepositLoading] = useState(false);
  
  // Withdrawal state
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawWallet, setWithdrawWallet] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await paymentAPI.getTransactions();
      setTransactions(res.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  }, []);

  const fetchWalletAddress = useCallback(async () => {
    try {
      const res = await paymentAPI.getWalletAddress();
      setWalletAddress(res.data.wallet_address);
    } catch (error) {
      console.error('Failed to fetch wallet address:', error);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
    fetchWalletAddress();
  }, [fetchTransactions, fetchWalletAddress]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      toast.success('Wallet address copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy address');
    }
  };

  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount < 10) {
      toast.error('Minimum deposit is $10');
      return;
    }
    
    if (!depositProof) {
      toast.error('Please upload payment proof (screenshot of transaction)');
      return;
    }

    setDepositLoading(true);
    try {
      await paymentAPI.createDepositWithProof(amount, depositTxHash || null, depositProof);
      toast.success('Deposit request with proof submitted! Awaiting admin approval.');
      
      // Refresh transactions (balance won't update until approved)
      fetchTransactions();
      setDepositAmount('100');
      setDepositTxHash('');
      setDepositProof(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process deposit');
    } finally {
      setDepositLoading(false);
    }
  };

  const handleProofChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPEG, PNG, and PDF files are allowed');
        return;
      }
      setDepositProof(file);
      toast.success('Payment proof selected');
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < 10) {
      toast.error('Minimum withdrawal is $10');
      return;
    }
    if (!withdrawWallet) {
      toast.error('Please enter your USDT TRC20 wallet address');
      return;
    }
    if (!withdrawWallet.startsWith('T') || withdrawWallet.length !== 34) {
      toast.error('Invalid TRC20 wallet address');
      return;
    }
    if (amount > (user?.balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }

    setWithdrawLoading(true);
    try {
      await paymentAPI.createWithdrawal(amount, withdrawWallet);
      toast.success('Withdrawal request submitted! Processing within 24 hours.');
      
      // Refresh user data and transactions
      const userRes = await authAPI.getMe();
      updateUser(userRes.data);
      fetchTransactions();
      setWithdrawAmount('');
      setWithdrawWallet('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process withdrawal');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const quickAmounts = [100, 250, 500, 1000];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return 'badge-success';
      case 'pending':
        return 'badge-warning';
      default:
        return 'badge-danger';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-red-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex" data-testid="wallet-page">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      
      <main className="flex-1 min-w-0">
        <header className="h-14 border-b border-zinc-800 flex items-center px-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-zinc-400 hover:text-white mr-4"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold text-white font-['Outfit']">Wallet</h1>
        </header>

        <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
          {/* Balance Cards */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Live Balance</div>
                  <div className="text-2xl font-mono font-semibold text-white" data-testid="live-balance">
                    {formatCurrency(user?.balance || 0)}
                  </div>
                </div>
              </div>
              <div className="text-xs text-zinc-500">
                Available for live trading
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Demo Balance</div>
                  <div className="text-2xl font-mono font-semibold text-white" data-testid="demo-balance">
                    {formatCurrency(user?.demo_balance || 0)}
                  </div>
                </div>
              </div>
              <div className="text-xs text-zinc-500">
                Virtual funds for practice trading
              </div>
            </div>
          </div>

          {/* Deposit / Withdraw Tabs */}
          <div className="glass-card p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="deposit" data-testid="deposit-tab">Deposit</TabsTrigger>
                <TabsTrigger value="withdraw" data-testid="withdraw-tab">Withdraw</TabsTrigger>
              </TabsList>

              {/* Deposit Tab */}
              <TabsContent value="deposit" className="space-y-6">
                {/* USDT Wallet Address */}
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-xs font-bold">
                      ₮
                    </div>
                    <span className="font-semibold text-green-400">USDT TRC20 Deposit Address</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-3 rounded-lg bg-zinc-900/80 text-white font-mono text-sm break-all" data-testid="wallet-address">
                      {walletAddress}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyToClipboard}
                      className="shrink-0"
                      data-testid="copy-address-btn"
                    >
                      {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-400 mt-2">
                    Send only USDT on TRC20 network. Other tokens will be lost.
                  </p>
                </div>

                {/* Deposit Amount */}
                <div>
                  <Label className="text-zinc-400 mb-2 block">Deposit Amount (USDT)</Label>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {quickAmounts.map((amount) => (
                      <Button
                        key={amount}
                        variant={depositAmount === String(amount) ? 'default' : 'outline'}
                        onClick={() => setDepositAmount(String(amount))}
                        className="font-mono"
                        data-testid={`quick-amount-${amount}`}
                      >
                        ${amount}
                      </Button>
                    ))}
                  </div>
                  <Input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    placeholder="Enter amount"
                    min="10"
                    className="bg-zinc-900/50 border-zinc-800 text-white font-mono text-lg"
                    data-testid="deposit-amount-input"
                  />
                </div>

                {/* Transaction Hash (Optional) */}
                <div>
                  <Label className="text-zinc-400 mb-2 block">Transaction Hash (Optional)</Label>
                  <Input
                    type="text"
                    value={depositTxHash}
                    onChange={(e) => setDepositTxHash(e.target.value)}
                    placeholder="Enter TRC20 transaction hash for faster verification"
                    className="bg-zinc-900/50 border-zinc-800 text-white font-mono text-sm"
                    data-testid="deposit-txhash-input"
                  />
                </div>

                {/* Payment Proof Upload */}
                <div>
                  <Label className="text-zinc-400 mb-2 block">Payment Proof (Required)</Label>
                  <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer">
                      <div className={`p-4 rounded-lg border-2 border-dashed transition-colors ${
                        depositProof 
                          ? 'border-green-500/50 bg-green-500/10' 
                          : 'border-zinc-700 hover:border-zinc-600 bg-zinc-900/50'
                      }`}>
                        <div className="flex items-center justify-center gap-2">
                          {depositProof ? (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-400" />
                              <span className="text-green-400 text-sm">{depositProof.name}</span>
                            </>
                          ) : (
                            <>
                              <ArrowUpRight className="w-5 h-5 text-zinc-400" />
                              <span className="text-zinc-400 text-sm">Click to upload payment screenshot</span>
                            </>
                          )}
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                        onChange={handleProofChange}
                        className="hidden"
                        data-testid="deposit-proof-input"
                      />
                    </label>
                    {depositProof && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDepositProof(null)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">
                    Upload a screenshot of your transaction. Max 5MB (JPEG, PNG, PDF)
                  </p>
                </div>

                <Button
                  onClick={handleDeposit}
                  disabled={depositLoading || !depositAmount || parseFloat(depositAmount) < 10 || !depositProof}
                  className="w-full glow-primary py-6 text-base"
                  data-testid="deposit-btn"
                >
                  {depositLoading ? (
                    <>
                      <span className="spinner mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ArrowDownRight className="w-5 h-5 mr-2" />
                      Submit Deposit Request ${depositAmount || 0}
                    </>
                  )}
                </Button>
              </TabsContent>

              {/* Withdraw Tab */}
              <TabsContent value="withdraw" className="space-y-6">
                <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                  <p className="text-sm text-zinc-300">
                    <span className="font-semibold">Available for withdrawal:</span>{' '}
                    <span className="font-mono text-white">{formatCurrency(user?.balance || 0)}</span>
                  </p>
                </div>

                {/* Withdrawal Amount */}
                <div>
                  <Label className="text-zinc-400 mb-2 block">Withdrawal Amount (USDT)</Label>
                  <Input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder="Enter amount (min $10)"
                    min="10"
                    max={user?.balance || 0}
                    className="bg-zinc-900/50 border-zinc-800 text-white font-mono text-lg"
                    data-testid="withdraw-amount-input"
                  />
                </div>

                {/* Wallet Address */}
                <div>
                  <Label className="text-zinc-400 mb-2 block">Your USDT TRC20 Wallet Address</Label>
                  <Input
                    type="text"
                    value={withdrawWallet}
                    onChange={(e) => setWithdrawWallet(e.target.value)}
                    placeholder="Enter your TRC20 wallet address (starts with T)"
                    className="bg-zinc-900/50 border-zinc-800 text-white font-mono text-sm"
                    data-testid="withdraw-wallet-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    TRC20 addresses start with &quot;T&quot; and are 34 characters long
                  </p>
                </div>

                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-xs text-yellow-400">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Withdrawals are processed within 24 hours
                  </p>
                </div>

                <Button
                  onClick={handleWithdraw}
                  disabled={withdrawLoading || !withdrawAmount || !withdrawWallet || parseFloat(withdrawAmount) < 10}
                  className="w-full py-6 text-base bg-red-600 hover:bg-red-700"
                  data-testid="withdraw-btn"
                >
                  {withdrawLoading ? (
                    <>
                      <span className="spinner mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ArrowUpRight className="w-5 h-5 mr-2" />
                      Request Withdrawal ${withdrawAmount || 0}
                    </>
                  )}
                </Button>
              </TabsContent>
            </Tabs>
          </div>

          {/* Transaction History */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-semibold text-white mb-6">Transaction History</h2>
            
            {transactions.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No transactions yet</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-3 pr-4" data-testid="transaction-list">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between p-4 rounded-lg bg-zinc-800/30">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded ${tx.type === 'deposit' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                          {tx.type === 'deposit' ? (
                            <ArrowDownRight className="w-4 h-4 text-green-400" />
                          ) : (
                            <ArrowUpRight className="w-4 h-4 text-red-400" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-white capitalize">{tx.type}</div>
                          <div className="text-xs text-zinc-500">
                            {tx.network} • {formatDate(tx.created_at)}
                          </div>
                          {tx.status === 'rejected' && (
                            <div className="text-xs text-red-400 mt-1">
                              Rejected - Please submit a new request
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-mono ${tx.type === 'deposit' ? 'text-green-400' : 'text-red-400'}`}>
                          {tx.type === 'deposit' ? '+' : '-'}{formatCurrency(tx.amount)}
                        </div>
                        <div className="flex items-center gap-1 justify-end">
                          {getStatusIcon(tx.status)}
                          <span className={`badge ${getStatusBadge(tx.status)}`}>
                            {tx.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
